/**
 * @fileoverview IndexedDB 本地日志（插件，可选）
 *
 * 用途：仅在 www.peerup.pro 生产环境、公司内部用户开启，将日志落到 IndexedDB，
 * 便于现场抓取用户日志排障（window.__downloadLog 下载）。
 *
 * 与原 web/accounts 实现完全对齐：
 * - enabled 判定：host === 'www.peerup.pro'
 * - 脚本加载：<script> 注入 `${staticUrl}/static/idb-logger/idb-logger.min.js`（原 loadJs 逻辑）
 * - 库名：`peerup-${env}-logger`
 * - uid 来源：getClientId()（@peerup/utils）
 *
 * 所需环境信息（env / staticUrl）从 LoggerConfig 顶层读取，接入方在 createLogger 时传入即可。
 */
import { getClientId } from '@peerup/utils'

import type { LoggerConfig } from '../core/types'

/** idb-logger 实例类型（最小契约，避免引入其类型依赖） */
interface IDBLoggerInstance {
  log: (...args: unknown[]) => void
  error: (...args: unknown[]) => void
  warn: (...args: unknown[]) => void
  info: (...args: unknown[]) => void
  download: () => void
}

/** idb-logger 构造器契约（UMD bundle 挂到 window） */
type IDBLoggerCtor = new (options?: {
  id?: string
  useConsole?: boolean
  maxRecords?: number
  baseInfo?: Record<string, string>
}) => IDBLoggerInstance

declare global {
  interface Window {
    IDBLogger?: IDBLoggerCtor
    __downloadLog?: () => void
  }
}

/** 脚本加载缓存（避免重复加载，对齐原 loadJs 的 scriptMap 逻辑） */
const scriptCache = new Map<string, Promise<void>>()

/**
 * <script> 注入加载外部脚本（对齐原 loadJs 实现）
 * @description 加载过的 URL 缓存其 Promise，重复调用直接复用
 */
function loadScript(src: string): Promise<void> {
  const cached = scriptCache.get(src)
  if (cached) return cached
  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.src = src
    script.onload = () => resolve()
    script.onerror = () => {
      scriptCache.delete(src)
      reject(new Error(`Failed to load script: ${src}`))
    }
    document.body.appendChild(script)
  })
  scriptCache.set(src, promise)
  return promise
}

/**
 * 默认开启判定：仅 www.peerup.pro 生产环境
 * @description 与原 web/accounts LocalLogger 的 canLog 逻辑一致
 */
const defaultEnabled = () => typeof window !== 'undefined' && window.location.host === 'www.peerup.pro'

/**
 * 本地日志
 * @description 仅用于 www.peerup.pro 生产环境、公司内部用户。
 * @param loggerConfig 接入方 LoggerConfig（取 env / staticUrl / source）
 */
export class LocalLogger {
  private logger?: IDBLoggerInstance

  /** 是否开启 */
  readonly enabled: boolean

  /**
   * @param loggerConfig 接入方 LoggerConfig（取 env / staticUrl / source）。
   *   LocalLogger 只读取 env/staticUrl，不感知接入方 EventMap，用默认泛型即可。
   */
  constructor(private readonly loggerConfig: LoggerConfig) {
    this.enabled = defaultEnabled()
    if (this.enabled) {
      this.#init().catch(console.error)
    }
  }

  async #init() {
    const { env, staticUrl } = this.loggerConfig

    // 懒加载 idb-logger 脚本（原实现：loadJs(`${STATIC_URL}/static/idb-logger/idb-logger.min.js`)）
    if (staticUrl) {
      await loadScript(`${staticUrl}/static/idb-logger/idb-logger.min.js`)
    }

    const IDBLogger = window.IDBLogger
    if (!IDBLogger) {
      console.warn('[🚨Logger] IDBLogger not found on window, skip local logging')
      return
    }

    this.logger = new IDBLogger({
      useConsole: false,
      // 库名按环境隔离（原实现：peerup-${VITE_APP_ENV}-logger）
      id: `peerup-${env?.toLowerCase()}-logger`,
      baseInfo: {
        uid: await getClientId(),
        ua: ' ',
      },
    })

    window.__downloadLog = () => this.logger?.download()
  }

  log(...args: unknown[]) {
    if (!this.enabled) return
    this.logger?.log(...args)
  }

  error(...args: unknown[]) {
    if (!this.enabled) return
    this.logger?.error(...args)
  }

  warn(...args: unknown[]) {
    if (!this.enabled) return
    this.logger?.warn(...args)
  }

  info(...args: unknown[]) {
    if (!this.enabled) return
    this.logger?.info(...args)
  }
}
